import { Engine } from './core/engine.js';
import { createConfig } from './core/config.js';

import { RenderSystem } from './render/index.js';
import { MaterialSystem } from './materials/index.js';
import { SkySystem } from './sky/index.js';
import { WorldSystem } from './world/index.js';
import { PhysicsSystem } from './physics/index.js';
import { PlayerSystem } from './player/index.js';
import { WeaponSystem } from './weapons/index.js';
import { FxSystem } from './fx/index.js';
import { AiSystem } from './ai/index.js';
import { UiSystem } from './ui/index.js';
import { AudioSystem } from './audio/index.js';
import { NetSystem } from './net/index.js';
import { LobbySystem } from './net/lobby.js';
import { MainMenu } from './ui/mainmenu.js';
import { QUALITY_KEY } from './ui/menu.js';
import { IDLE_KEY, IDLE_MODES } from './core/idle.js';
import { readStoredLoadout } from './ui/armory.js';

import { installShotApi } from './dev/shots.js';
import { prewarm } from './core/prewarm.js';

const params = new URLSearchParams(location.search);
const capture = params.get('capture') === '1';
// Deterministic shutter for the pixel gate: the engine does not schedule its own
// frames, the driver advances exactly N of them through window.__PUMP__. Opt-in,
// because tools that measure real frame pacing (tools/perf.mjs) need the loop to
// free-run. See the long comment in src/dev/shots.js.
const lockstep = capture && params.get('lockstep') === '1';

/**
 * Quality resolution order: an explicit `?q=` (the capture harness and shared
 * links use it), then whatever the player last picked in the pause menu, then
 * `auto` — which asks src/core/hwtier.js what this machine can take.
 *
 * A capture run is pinned to `ultra` regardless: the pixel gate compares frames
 * byte for byte, and letting the baseline depend on the GPU of whoever ran it
 * would make every shot unreproducible.
 */
const storedQuality = (() => {
  try {
    return globalThis.localStorage?.getItem(QUALITY_KEY) || null;
  } catch {
    return null;
  }
})();

/**
 * The idle governor is pinned OFF for a capture, exactly like quality is pinned
 * to ultra. A headless browser's focus and visibility are properties of the
 * harness, not of the game, and the pixel gate must never be able to skip a
 * frame because the window driving it happened to be in the background.
 */
const storedIdle = (() => {
  try {
    const v = globalThis.localStorage?.getItem(IDLE_KEY);
    return IDLE_MODES.includes(v) ? v : null;
  } catch {
    return null;
  }
})();

// The armory writes this before a reload, exactly like quality/idle above —
// only the equipped loadout (+ melee) is ever built into the viewmodel (see
// WeaponSystem.init()), so it has to be known before the engine, not chosen
// mid-match.
const storedLoadout = readStoredLoadout();

const config = createConfig({
  quality: params.get('q') ?? (capture ? 'ultra' : storedQuality ?? 'auto'),
  deterministic: capture,
  idleMode: capture ? 'off' : params.get('idle') ?? storedIdle ?? undefined,
  loadout: storedLoadout ?? undefined,
});

/**
 * Is this page running a bundle the server no longer serves? Reload once if so.
 *
 * Guarded by sessionStorage: a boot failure that is NOT staleness must never
 * become a reload loop, and one attempt is enough to rule staleness out.
 */
async function reloadIfStaleBuild() {
  try {
    if (sessionStorage.getItem('rift:stale-reload') === '1') return false;
    const html = await fetch(new URL('/', location.href), { cache: 'reload' }).then((r) => r.text());
    const served = /assets\/index-[A-Za-z0-9_-]+\.js/.exec(html)?.[0];
    if (!served) return false;
    if (import.meta.url.endsWith(served.slice(served.lastIndexOf('/') + 1))) return false;
    console.warn(`[boot] running a stale build; the server serves ${served} — reloading once`);
    sessionStorage.setItem('rift:stale-reload', '1');
    location.reload();
    return true;
  } catch {
    return false;
  }
}

const canvas = document.getElementById('game');

const engine = new Engine({ canvas, config });

// The homescreen is deliberately mounted before the 3D systems initialize.
// World generation and shader warmup can take a noticeable moment on a first
// visit; the player should see the game front end immediately, never an empty
// black canvas. UiSystem adopts this same instance once it has initialized and
// wires the actual match-start callbacks onto it.
const earlyHome = new MainMenu(document.getElementById('ui'), engine.ctx);
engine.ctx.homeMenu = earlyHome;
// A capture is a picture of the GAME. The home screen covers the whole canvas,
// so showing it here puts the menu in front of every shot the review harness
// takes — which is exactly what it did until this guard existed.
if (!capture) earlyHome.show();

// Registration order is irrelevant — Registry topo-sorts on static deps.
engine
  .add(RenderSystem)
  .add(MaterialSystem)
  .add(SkySystem)
  .add(WorldSystem)
  .add(PhysicsSystem)
  .add(PlayerSystem)
  .add(WeaponSystem)
  .add(FxSystem)
  .add(AiSystem)
  .add(UiSystem)
  .add(AudioSystem)
  // Multiplayer disables itself in capture and deterministic runs, so the
  // pixel gate never depends on who happens to be online. See src/net/.
  .add(NetSystem)
  .add(LobbySystem);

// An init that never settles renders nothing and logs nothing — the page just
// sits there looking alive, because its timers keep running. Say which
// subsystem is holding it up rather than leaving the player staring at a menu
// whose buttons cannot work yet.
const bootStall = setTimeout(() => {
  const stage = engine.ctx.bootStage ?? 'unknown';
  console.warn(`[boot] "${stage}" has not finished initializing after 25s`);
  document.body.insertAdjacentHTML(
    'beforeend',
    `<div id="rift-boot-stall" style="position:fixed;left:0;right:0;bottom:0;z-index:9998;
       padding:10px 16px;background:rgba(40,26,4,.94);color:#ffb02a;
       font:12px/1.5 ui-monospace,monospace;letter-spacing:.08em">
     STILL LOADING — "${stage}" is taking longer than expected. If this does not clear, reload the page.</div>`
  );
}, 25000);

try {
  await engine.init();
} catch (err) {
  console.error('[boot] init failed', err);
  // nginx serves index.html with no Cache-Control, so a browser can hold an old
  // copy indefinitely and keep importing chunk hashes from a build that has
  // moved on. That bricks boot in a way no amount of clicking fixes, and
  // "press Ctrl+Shift+R" is not a fix a player should have to know. Check
  // whether the served page points at a different bundle than the one running
  // and, if so, reload exactly once.
  if (await reloadIfStaleBuild()) throw err;
  document.body.insertAdjacentHTML(
    'beforeend',
    `<pre style="position:fixed;inset:0;padding:2rem;color:#f66;background:#000;
       font:12px/1.5 ui-monospace,monospace;overflow:auto;z-index:9999;white-space:pre-wrap">
BOOT FAILURE\n\n${err.stack ?? err.message}</pre>`
  );
  throw err;
} finally {
  clearTimeout(bootStall);
  document.getElementById('rift-boot-stall')?.remove();
}

const shotApi = installShotApi(engine, { capture, lockstep });

// Only reveal the homescreen after every gameplay system is ready. In
// particular, this lets its ONLINE/OFFLINE choice configure the already
// initialized multiplayer and AI systems before any player control begins.
if (!capture) engine.ctx.peek('ui')?.showHome?.();

// Compile every shader permutation before the frame loop starts. Measured: without
// this, 86 programs compile lazily during play, up to 30 on one frame, producing
// 3.1-3.9 SECOND stalls. See src/core/prewarm.js.
//
// ON BY DEFAULT since the capture path was made frame-deterministic; opt out with
// `?prewarm=0`. It is now PROVEN pixel-neutral: `tools/baseline.mjs` with
// `--query=prewarm=0` vs `--query=prewarm=1` reports identical:true on all 11
// shots (0 changed pixels, maxDelta 0). The two things that previously made the
// ~1.4 s pre-warm spend look like a visual change were both boot-duration
// couplings OUTSIDE the subsystems: (1) the shutter frame index was latency-bound
// because the engine kept stepping through the driver's round trips — fixed by
// lockstep in src/dev/shots.js; (2) `will-change: transform` on the compass strip
// cached a composited-layer raster taken at a wall-clock-dependent moment — fixed
// in src/ui/style.js.
// `?prewarm=0` disables it; `?prewarm=<ms>` sets the budget (see DEFAULT_BUDGET_MS
// in src/core/prewarm.js for why an unbounded pre-warm is not an option).
//
// THE FRAME LOOP MUST NOT BE RUNNING WHILE THIS RUNS. The pre-warm mutates the
// scene to reach the programs it needs: `world.prewarmMaterials()` swaps in
// `scene.overrideMaterial` and `ai.prewarmMaterials()` adds throwaway soldier
// and grenade meshes. It is pixel-neutral only because nothing draws meanwhile.
// Starting the loop first renders exactly those mutations — a world drawn
// through the depth override with two proxy meshes parked in front of it.
//
// A player who already pressed GO must not be made to sit through it either, so
// a queued deploy skips the pre-warm outright rather than rendering underneath
// it. The cost is lazy shader compilation on the first frames that need it.
const prewarmParam = params.get('prewarm');
const deployQueued = !capture && Boolean(engine.ctx.peek('ui')?.hasQueuedDeploy?.());
let warmup;
try {
  warmup = deployQueued
    ? { ok: false, reason: 'skipped: a deploy was queued during boot' }
    : prewarmParam === '0'
      ? { ok: false, reason: 'disabled by ?prewarm=0' }
      : await prewarm(engine, Number(prewarmParam) > 0 ? { budgetMs: Number(prewarmParam) } : {});
} catch (err) {
  // An optimisation must never strand boot. Everything below still has to run:
  // the frame loop for capture, the __READY__ handshake, __ENGINE__.
  console.warn('[boot] prewarm failed', err);
  warmup = { ok: false, reason: `threw: ${err?.message ?? err}` };
}
console.info('[boot] prewarm', warmup);
window.__PREWARM__ = warmup;

engine.start();

// Safe here and not before: every scene mutation the pre-warm makes is undone,
// so the first frame this match renders is the real one.
if (!capture) engine.ctx.peek('ui')?.flushQueuedDeploy?.();

// Capture harness handshake: only flag ready once a frame has actually landed.
//
// BOOT_FRAMES is deliberately a frame COUNT, not a rAF race. In lockstep mode the
// engine has no loop of its own, so we hand-pump exactly this many frames and only
// then raise __READY__; the shot is therefore always applied at engine frame 3, no
// matter how long boot (or pre-warm) took in wall-clock terms.
const BOOT_FRAMES = 3;
if (lockstep) {
  await shotApi.pump(BOOT_FRAMES);
  window.__READY__ = true;
} else {
  let warm = 0;
  const readyProbe = () => {
    if (++warm >= BOOT_FRAMES) {
      window.__READY__ = true;
      return;
    }
    requestAnimationFrame(readyProbe);
  };
  requestAnimationFrame(readyProbe);
}

window.__ENGINE__ = engine;

if (import.meta.hot) {
  import.meta.hot.dispose(() => engine.dispose());
}
